/**
 * ONE place for every Shopify cart link on the site.
 *
 * Paste either the full cart link (https://shop.gritvuegear.com/cart/45095500546141:1)
 * or just the variant ID (45095500546141). The quantity at the end is set
 * automatically, so one link per product is enough.
 *
 * An empty value never sends anyone to a broken page: the button shows a short
 * "checkout link not connected yet" notice instead.
 *
 * PUBLIC products appear as cards. PRIVATE products only appear inside the
 * checkout negotiation at a specific decision point, never as public cards.
 */
export const PRODUCT_LINKS = {
  // ---------- Single X1 ----------
  /** SINGLE_X1_VARIANT_ID · PUBLIC · 1 X1 · $49.95 · one-year coverage · shipping calculated at checkout */
  singleX1: 'https://shop.gritvuegear.com/cart/45095500546141:1',
  /** SINGLE_X1_LIFETIME_NORMAL_6990_VARIANT_ID · PUBLIC option · 1 X1 + Lifetime · $69.90 · shipping calculated at checkout */
  singleX1LifetimeNormal6990: '',
  /** SINGLE_X1_LIFETIME_PRIVATE_5990_VARIANT_ID · PRIVATE · 1 X1 + Lifetime · $59.90 · FREE SHIPPING */
  singleX1LifetimePrivate5990: '',
  /** SINGLE_X1_LIFETIME_FINAL_5790_VARIANT_ID · PRIVATE · 1 X1 + Lifetime · $57.90 · FREE SHIPPING */
  singleX1LifetimeFinal5790: '',
  /** SINGLE_X1_LIFETIME_FREE_SHIPPING_RESCUE_6990_VARIANT_ID · PRIVATE retention · 1 X1 + Lifetime · $69.90 · FREE SHIPPING */
  singleX1LifetimeFreeShipping6990: '',

  // ---------- 3 X1 and its private expansion ----------
  /** THREE_X1_PUBLIC_9990_VARIANT_ID · PUBLIC · Buy 2 Get 1 Free · 3 X1 · $99.90 · lifetime on all 3 · FREE SHIPPING */
  threeX1Public9990: 'https://shop.gritvuegear.com/cart/45095660224605:1',
  /** FOUR_X1_PRIVATE_11990_VARIANT_ID · PRIVATE · 4 X1 · $119.90 · lifetime on all 4 · FREE SHIPPING */
  fourX1Private11990: '',
  /** FIVE_X1_PRIVATE_14990_VARIANT_ID · PRIVATE · 5 X1 · $149.90 · lifetime on all 5 · FREE SHIPPING */
  fiveX1Private14990: '',
  /** SIX_X1_PRIVATE_17990_VARIANT_ID · PRIVATE · 6 X1 · $179.90 · lifetime on all 6 · FREE SHIPPING */
  sixX1Private17990: '',

  // ---------- 6 X1 and its private expansion ----------
  /** SIX_X1_PUBLIC_19980_VARIANT_ID · PUBLIC · Buy 4 Get 2 Free · 6 X1 · $199.80 · lifetime on all 6 · FREE SHIPPING */
  sixX1Public19980: 'https://shop.gritvuegear.com/cart/45095747879005:1',
  /** EIGHT_X1_PRIVATE_23990_VARIANT_ID · PRIVATE · 8 X1 · $239.90 · lifetime on all 8 · FREE SHIPPING */
  eightX1Private23990: '',
  /** TEN_X1_PRIVATE_29990_VARIANT_ID · PRIVATE · 10 X1 · $299.90 · lifetime on all 10 · FREE SHIPPING */
  tenX1Private29990: '',
  /** TWELVE_X1_PRIVATE_35990_VARIANT_ID · PRIVATE · 12 X1 · $359.90 · lifetime on all 12 · FREE SHIPPING */
  twelveX1Private35990: '',

  // ---------- Business ----------
  /** THIRTY_X1_PUBLIC_99900_VARIANT_ID · PUBLIC · Buy 20 Get 10 Free · 30 X1 · $999.00 · lifetime on all 30 · FREE SHIPPING */
  thirtyX1Public99900: 'https://shop.gritvuegear.com/cart/45095754858589:1',
  /** FORTY_X1_BUSINESS_PRIVATE_119900_VARIANT_ID · PRIVATE · 40 X1 · $1,199 · lifetime on all 40 · FREE SHIPPING */
  fortyX1BusinessPrivate119900: '',
  /** FIFTY_X1_BUSINESS_PRIVATE_149900_VARIANT_ID · PRIVATE · 50 X1 · $1,499 · lifetime on all 50 · FREE SHIPPING */
  fiftyX1BusinessPrivate149900: '',
  /** SIXTY_X1_BUSINESS_PRIVATE_179900_VARIANT_ID · PRIVATE · 60 X1 · $1,799 · lifetime on all 60 · FREE SHIPPING */
  sixtyX1BusinessPrivate179900: '',
}

export type ProductLinkKey = keyof typeof PRODUCT_LINKS

const SHOP_CART_URL = 'https://shop.gritvuegear.com/cart'

/** The Shopify variant ID inside a link (or the link itself if it is a bare ID). */
export function variantIdOf(link: string): string {
  const match = link.match(/(\d{6,})(?::\d+)?\/?$/)
  return match ? match[1] : link.trim()
}

/** Cart URL for a product at a given quantity, or '' when the link is not set yet. */
export function cartLink(key: ProductLinkKey, quantity = 1): string {
  const raw = PRODUCT_LINKS[key].trim()
  if (!raw) return ''
  const id = variantIdOf(raw)
  if (!/^\d+$/.test(id)) return raw
  const base = /^https?:\/\//.test(raw) ? raw.replace(/\/cart\/.*$/, '/cart') : SHOP_CART_URL
  return `${base}/${id}:${quantity}`
}
